import { useState } from "react";
import { logger } from "../lib/logger";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Zap, Loader2, AlertCircle, Sparkles } from "lucide-react";
import { useAuth } from "../contexts/AuthContext";
import { formatError } from "../lib/api";

export default function LoginPage() {
  const { login } = useAuth();
  const nav = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(false);

  const onSubmit = async (ev) => {
    ev.preventDefault();
    setErr("");
    setLoading(true);
    try {
      await login(email, password);
      toast.success("Welcome back!");
      nav("/dashboard");
    } catch (x) {
      logger.error("Login failed", x);
      setErr(formatError(x.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-white flex" data-testid="login-page">
      <div className="flex-1 flex items-center justify-center p-8">
        <div className="w-full max-w-sm animate-fade-in">
          <div className="flex items-center gap-2 mb-10 cursor-pointer" onClick={() => nav("/")}>
            <Zap className="w-6 h-6 text-brand" strokeWidth={2.5} />
            <span className="font-black text-xl tracking-tighter text-zinc-950 font-heading">CopyForge</span>
          </div>
          <h1 className="text-3xl font-black tracking-tighter text-zinc-950 mb-2 font-heading">Welcome back</h1>
          <p className="text-zinc-500 mb-8">Sign in to continue.</p>
          {err && (
            <div className="bg-red-50 border border-red-200 text-red-700 text-sm p-3 mb-6 flex items-center gap-2" data-testid="login-error">
              <AlertCircle className="w-4 h-4 flex-shrink-0" />
              {err}
            </div>
          )}
          <form onSubmit={onSubmit} className="space-y-4">
            <div>
              <label className="text-sm font-medium text-zinc-700 block mb-1">Email</label>
              <input data-testid="login-email-input" type="email" value={email} onChange={(x) => setEmail(x.target.value)} required className="w-full bg-zinc-50 border border-zinc-200 px-4 py-3 text-zinc-900 focus:border-brand focus:ring-1 focus:ring-brand outline-none" placeholder="you@company.com" />
            </div>
            <div>
              <label className="text-sm font-medium text-zinc-700 block mb-1">Password</label>
              <input data-testid="login-password-input" type="password" value={password} onChange={(x) => setPassword(x.target.value)} required className="w-full bg-zinc-50 border border-zinc-200 px-4 py-3 text-zinc-900 focus:border-brand focus:ring-1 focus:ring-brand outline-none" placeholder="Password" />
            </div>
            <button data-testid="login-submit-btn" type="submit" disabled={loading} className="w-full bg-brand text-white font-semibold py-3 hover:bg-brand-hover flex items-center justify-center gap-2 disabled:opacity-50">
              {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : "Sign In"}
            </button>
          </form>
          <p className="text-sm text-zinc-500 mt-6 text-center">
            No account? <button data-testid="go-to-register-link" onClick={() => nav("/register")} className="text-brand font-semibold hover:underline">Sign up free</button>
          </p>
        </div>
      </div>
      <div className="hidden lg:flex flex-1 bg-zinc-950 items-center justify-center p-12 relative overflow-hidden">
        <div className="absolute inset-0 bg-[linear-gradient(rgba(0,47,167,0.1)_1px,transparent_1px),linear-gradient(90deg,rgba(0,47,167,0.1)_1px,transparent_1px)] bg-[size:40px_40px]" />
        <div className="max-w-md relative z-10">
          <Sparkles className="w-12 h-12 text-brand mb-6" />
          <h2 className="text-3xl font-black tracking-tighter text-white mb-4 font-heading">
            AI that writes copy<br />so you don't have to.
          </h2>
          <p className="text-zinc-400 leading-relaxed">Join thousands using CopyForge.</p>
        </div>
      </div>
    </div>
  );
}
